import * as React from 'react';

import { OverlayTriggerContext, toast } from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { useSetAnalyticsScreen } from '../../navigation';
import { useGuidedTour } from '../Onboarding';
import { SpaceContext } from '../Space';
import { ImportContentDialog, type ImportContentDialogProps } from './ImportContentDialog';

export interface ImportInSpaceDialogProps
    extends Pick<ImportContentDialogProps, 'defaultSource' | 'onStart'> {
    /**
     * Page to insert imported content under.
     */
    page?: string;
}

/**
 * Dialog to import some content directly into an existing space.
 */
export function ImportInSpaceDialog(props: ImportInSpaceDialogProps) {
    const { page, defaultSource, onStart } = props;

    const context = React.useContext(SpaceContext);
    assert(context, 'ImportInSpaceDialog must be used within a space context');
    const { space, changeRequest, analyticsContext } = context;

    const importTour = useGuidedTour('cr-import');

    const triggerContext = React.useContext(OverlayTriggerContext);
    assert(triggerContext, 'ImportInSpaceDialog must be used within an DialogRoot');
    const { isOpen } = triggerContext;

    useSetAnalyticsScreen(
        isOpen
            ? {
                  screen: 'space_import',
                  ...analyticsContext,
              }
            : undefined,
        [analyticsContext, isOpen]
    );

    return (
        <ImportContentDialog
            defaultSource={defaultSource}
            trigger="content"
            target={{
                page,
                space,
                changeRequest: changeRequest?.id,
            }}
            onStart={async (target) => {
                await onStart?.(target);
                // We don't show the import tour if we are importing from a change request.
                importTour.markAsDone();
                toast.success(
                    'Your import has been started, you can track its progress in the table of contents.'
                );
            }}
        />
    );
}
